'use client';

import Link from 'next/link';
import { Compass, MessageSquare } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { EMessageRole, IConversation, IMessage, IDataPoolEntry } from '@/types';
import { AnalyticsService } from '@/_services/analytics.service.client';
import AssistantMessageWithTabs from '@/_components/AssistantMessageWithTabs/AssistantMessageWithTabs';
import { Button } from '@/_components/UIComponents/Button';
import DataDesk from '@/_components/DataDesk/DataDesk';

function UserMessage({ message }: { message: string }) {
	return (
		<div className='bg-secondary text-foreground ml-auto w-fit max-w-[90vw] rounded-lg p-3 break-words whitespace-pre-wrap sm:p-4 lg:max-w-[600px]'>
			{message}
		</div>
	);
}

interface PublicConversationProps {
	conversation: IConversation;
	messages: IMessage[];
	dataPoolEntries: IDataPoolEntry[];
	isAdminView?: boolean;
}

export default function PublicConversation({
	conversation,
	messages,
	dataPoolEntries,
	isAdminView
}: PublicConversationProps) {
	// Track shared conversation view on mount (skip for admin views to avoid skewing metrics)
	useEffect(() => {
		if (isAdminView) return;
		AnalyticsService.sharedConversationViewed({
			conversationId: conversation.id,
			conversationTitle: conversation.title || '',
			messageCount: messages.length,
			citationCount: conversation.totalCitations ?? 0
		});
	}, [conversation.id, conversation.title, conversation.totalCitations, messages.length, isAdminView]);

	// Group data pool entries by messageId for quick lookup
	const entriesByMessage = useMemo(() => {
		const map = new Map<string, IDataPoolEntry[]>();
		for (const entry of dataPoolEntries) {
			const arr = map.get(entry.messageId) ?? [];
			arr.push(entry);
			map.set(entry.messageId, arr);
		}
		return map;
	}, [dataPoolEntries]);
	// Note: No auto-scroll for public conversations - users should start reading from the top

	return (
		<div className='relative flex h-full w-full overflow-hidden'>
			{/* Chat content */}
			<div className='flex min-w-0 flex-1 flex-col overflow-hidden'>
				<section className='w-full flex-1 overflow-x-hidden overflow-y-auto pb-20'>
					<div className='mx-auto flex w-full max-w-3xl flex-col gap-3 p-2 break-words sm:gap-4 sm:p-4'>
						{messages.map((item, index) => (
							<div key={`${item.id}-${index}`}>
								{item.role === EMessageRole.HUMAN ? (
									<UserMessage message={item.text} />
								) : (
									<AssistantMessageWithTabs
										message={item.text}
										isStreaming={false}
										currentStatus={null}
										activities={item.agentActivities}
										citations={item.citations ?? []}
										dataPoolEntries={entriesByMessage.get(item.id) ?? []}
										tokenUsage={item.tokenUsage}
										messageId={item.id}
										feedback={item.feedback}
										readOnly={true}
									/>
								)}
							</div>
						))}
					</div>
				</section>

				{/* Compact read-only footer */}
				<div className='border-border bg-card/95 sticky bottom-0 w-full border-t px-3 py-2 sm:px-4'>
					<div className='mx-auto flex w-full max-w-3xl flex-col items-center justify-center gap-2 sm:flex-row sm:gap-3'>
						<span className='text-muted-foreground text-xs'>Read-only public conversation</span>
						<div className='flex items-center gap-2'>
							<Link href='/login'>
								<Button
									size='sm'
									variant='outline'
									className='flex items-center gap-1.5 text-xs'
								>
									<MessageSquare className='h-3 w-3' />
									Start your own
								</Button>
							</Link>
							<span className='text-muted-foreground text-xs'>or</span>
							<Link href='/discover'>
								<Button
									size='sm'
									variant='outline'
									className='flex items-center gap-1.5 text-xs'
								>
									<Compass className='h-3 w-3' />
									Explore more
								</Button>
							</Link>
						</div>
					</div>
				</div>
			</div>

			{/* Data Desk right panel */}
			<DataDesk entries={dataPoolEntries} />
		</div>
	);
}
